import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { Helmet } from "react-helmet";
import { Box, Typography, Alert, Fade } from "@mui/material";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../utils/colors";
import { startTrialLogin } from "../../actions/auth";
import Loader from "../../components/Loaders/Loader";
import Button from "../../components/Buttons/Button";
import {
  BORDER_RADIUS,
  PLAN_TYPE_CREDITS,
  PLAN_TYPES,
} from "../../utils/constants";
import { useTranslationApp } from "../../lib/i18next";
import {
  TRIAL_CONFIGURING_EXPERIENCE,
  TRIAL_PREPARING_PLATFORM,
  TRIAL_RETRY_ACCESS,
} from "../../i18n/keysTranslations";

const Trial = () => {
  const { theme } = useSelectorApp((state) => state.ui);
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [searchParams] = useSearchParams();
  const isRetry = searchParams.get("isRetry") === "true";
  const trialParam = searchParams.get("trial");
  const trial =
    trialParam === null || trialParam === undefined
      ? true
      : trialParam === "true";
  const planTypeParam = searchParams.get("planType");
  const planType =
    planTypeParam === null || planTypeParam === undefined
      ? PLAN_TYPE_CREDITS
      : planTypeParam;
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onHandleTrial = async () => {
      let trialValue = trial;
      if (typeof trialValue !== "boolean") {
        trialValue = true;
      }

      let planTypeValue = planType;
      if (
        typeof planTypeValue !== "string" ||
        !PLAN_TYPES.includes(planTypeValue)
      ) {
        planTypeValue = PLAN_TYPE_CREDITS;
      }

      const response = await dispatch(
        startTrialLogin({ trial: trialValue, planType: planTypeValue })
      );
      if (!response.ok) {
        setError(response.message || "Error al iniciar el trial");
      } else {
        navigate("/settings");
      }
    };

    onHandleTrial();
    //eslint-disable-next-line
  }, [dispatch]);

  const onRetry = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("isRetry", "true");
    window.location.href = currentUrl.toString();
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <Fade in={true} timeout={1000}>
        <Box
          sx={{
            width: "100%",
            maxWidth: "450px",
            py: 8,
            px: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            backgroundColor: "background.paper",
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            borderRadius: 4,
            boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.08)",
            textAlign: "center",
          }}
        >
          <Helmet>
            <title>{`${theme.nameBusiness} - ${t(TRIAL_CONFIGURING_EXPERIENCE)}`}</title>
          </Helmet>

          <Avatar
            src={theme.logoLogin}
            alt="Logo"
            variant="square"
            sx={{
              width: "auto",
              height: "auto",
              maxHeight: "80px",
              ".MuiAvatar-img": {
                maxWidth: "200px",
                objectFit: "contain",
              },
              mb: 6,
            }}
          />

          {!error ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Loader
                size={70}
                colorCustom={theme.palette.primary.main}
                hasMarginTop={false}
              />
              <Typography
                variant="h6"
                sx={{
                  mt: 4,
                  fontWeight: 600,
                  color: "text.primary",
                  letterSpacing: "0.5px",
                }}
              >
                {t(TRIAL_CONFIGURING_EXPERIENCE)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: "text.secondary",
                  maxWidth: "280px",
                }}
              >
                {t(TRIAL_PREPARING_PLATFORM)}
              </Typography>
            </Box>
          ) : (
            <Fade in={true}>
              <Box sx={{ width: "100%", mt: 2 }}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    backgroundColor: "fields.background",
                    color: "background.paperContrastText",
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    borderRadius: BORDER_RADIUS,
                    boxShadow: BOX_SHADOW_FIELDS,
                  }}
                >
                  {error}
                </Alert>
                {!isRetry && (
                  <Button fullWidth onClick={onRetry}>
                    {t(TRIAL_RETRY_ACCESS)}
                  </Button>
                )}
              </Box>
            </Fade>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default Trial;
